<script setup lang="ts">
import { computed, ref } from 'vue'
import { api } from '../api/client'
import PageBody from '../components/PageBody.vue'
import PageHeader from '../components/PageHeader.vue'
import { IconChevronLeft, IconChevronRight, IconRefresh } from '../components/Icons'
import { Table, Td, Th, Tr } from '../components/Table'
import { Badge, Button, EmptyState, ErrorState, Panel, SkeletonRows } from '../components/ui'
import { actionName, formatDateTime, relativeTime, shortID } from '../lib/format'
import { useAsync, useTitle } from '../lib/hooks'

const PAGE_SIZE = 50

useTitle('日志')
const offset = ref(0)
const expanded = ref<number | null>(null)
const { data, error, initial, loading, reload } = useAsync(
  () => api.listAudit(PAGE_SIZE, offset.value),
  () => [offset.value],
)

const entries = computed(() => data.value?.entries ?? [])
const total = computed(() => data.value?.total ?? 0)
const from = computed(() => (total.value === 0 ? 0 : offset.value + 1))
const to = computed(() => offset.value + entries.value.length)
</script>

<template>
  <PageHeader title="日志">
    <template #actions>
      <Button size="sm" :loading="loading && !initial" @click="reload">
        <IconRefresh :size="13" />
        刷新
      </Button>
    </template>
  </PageHeader>

  <PageBody>
    <Panel :title="`${total} 条`">
      <ErrorState v-if="error" :message="error.message" @retry="reload" />
      <SkeletonRows v-else-if="initial" :rows="10" :cols="4" />
      <EmptyState
        v-else-if="entries.length === 0"
        title="还没有审计记录"
        hint="管理员登录后台、调整权益或改动内容后，这里会留下痕迹。"
      />
      <template v-else>
        <Table>
          <template #head>
            <Tr>
              <Th class="w-[150px]">时间</Th>
              <Th class="w-[170px]">操作人</Th>
              <Th>操作</Th>
              <Th class="w-[190px]">对象</Th>
              <Th class="w-[130px]">请求 ID</Th>
            </Tr>
          </template>
          <template v-for="entry in entries" :key="entry.id">
            <Tr
              clickable
              :class="expanded === entry.id ? 'bg-hover' : undefined"
              @click="expanded = expanded === entry.id ? null : entry.id"
            >
              <Td mono class="whitespace-nowrap text-dim">
                <span :title="formatDateTime(entry.created_at)">{{ formatDateTime(entry.created_at) }}</span>
                <span class="ml-2 text-[11px] text-line-strong">{{ relativeTime(entry.created_at) }}</span>
              </Td>
              <Td class="truncate text-muted">
                <span :title="entry.actor_email">{{ entry.actor_email }}</span>
              </Td>
              <Td>
                <span class="text-text">{{ actionName(entry.action) }}</span>
                <span class="ml-2 font-mono text-[11px] text-dim">{{ entry.action }}</span>
              </Td>
              <Td mono class="text-muted whitespace-nowrap">
                <span :title="entry.target_id || undefined">
                  {{ entry.target_type }}{{ entry.target_id ? ` #${shortID(entry.target_id)}` : '' }}
                </span>
              </Td>
              <Td mono class="truncate text-dim">
                <span :title="entry.request_id">{{ entry.request_id || '—' }}</span>
              </Td>
            </Tr>
            <tr v-if="expanded === entry.id">
              <Td :col-span="5" class="bg-base/70">
                <pre
                  v-if="Object.keys(entry.detail ?? {}).length > 0"
                  class="overflow-x-auto rounded-md border border-line bg-base p-3 font-mono text-[11.5px] leading-relaxed text-muted"
                >{{ JSON.stringify(entry.detail, null, 2) }}</pre>
                <span v-else class="text-[12px] text-dim">这次操作没有附加明细</span>
              </Td>
            </tr>
          </template>
        </Table>

        <div v-if="offset > 0 || to < total" class="flex items-center justify-between border-t border-line px-4 py-3">
          <span class="tnum text-[12px] text-dim">{{ from }}–{{ to }} / {{ total }}</span>
          <span class="flex gap-2">
            <Button size="sm" :disabled="offset === 0" @click="offset = Math.max(0, offset - PAGE_SIZE)">
              <IconChevronLeft :size="13" />
              上一页
            </Button>
            <Button size="sm" :disabled="to >= total" @click="offset = offset + PAGE_SIZE">
              下一页
              <IconChevronRight :size="13" />
            </Button>
          </span>
        </div>
      </template>
    </Panel>

    <p class="mt-3 flex flex-wrap items-center gap-2 text-[11.5px] text-dim">
      <Badge tone="neutral">说明</Badge>
      日志保留操作人邮箱快照：账号被删除后，历史记录仍能追溯到底是谁做的。
    </p>
  </PageBody>
</template>
